import { uiLogger, authLogger } from "../../utils/logger";
import { redirectToApp } from "../../utils/redirectToApp";
import { JSX, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import useAppStore from '../../stores/useAppStore';
import { authApi } from '../../services/api/authApi';
import { authService } from '../../services/api';
import { handleApiError } from '../../services/api/errorHandler';
import { isUserNotConfirmedException } from '../../utils/cognitoErrorUtils';
import NewPasswordRequiredModal from './NewPasswordRequiredModal';
import toast from 'react-hot-toast';

interface FormData {
  email: string;
  password: string;
}

export default function LoginModal(): JSX.Element {
  const navigate = useNavigate();
  const {
    isLoginModalOpen,
    closeLoginModal,
    openRegistrationModal,
    openForgotPasswordModal,
    openEmailVerificationModal,
    setRegistrationData,
    setPendingEmailVerification,
    setVerificationFromLogin,
    showExistingUserMessage,
    setShowExistingUserMessage,
    prefillEmail,
    setPrefillEmail,
    login
  } = useAppStore();

  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [showNewPasswordModal, setShowNewPasswordModal] = useState<boolean>(false);
  const [newPasswordUsername, setNewPasswordUsername] = useState<string>('');

  const MOCK_MODE = import.meta.env.VITE_MOCK_MODE === 'true';

  // Effect to prefill email when modal opens with prefilled email
  useEffect(() => {
    if (isLoginModalOpen && prefillEmail) {
      setFormData(prev => ({
        ...prev,
        email: prefillEmail
      }));
      // Clear the prefill email after using it
      setPrefillEmail('');
    }
  }, [isLoginModalOpen, prefillEmail, setPrefillEmail]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
    // Clear existing user message when user starts typing
    if (showExistingUserMessage) setShowExistingUserMessage(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await authApi.login(formData.email, formData.password);

      if (response.success && response.user) {
        // Registration is always considered complete now
        
        // Registration is complete, proceed with normal login
        // Set user data with more complete information
        const userData = {
          ...response.user,
          // Add some demo data for better UX
          company: response.user?.company || 'N/A',
          role: response.user?.role || 'Admin',
          tenantId: response.tenantId || ''
        };

        login(userData);
        
        // Clear the existing user message flag on successful login
        setShowExistingUserMessage(false);
        
        closeLoginModal();
        
        // Redirect to app with fresh tokens
        await redirectToApp();
      } else if (response.challengeName === 'NEW_PASSWORD_REQUIRED') {
        // User needs to set a new password (admin-created user)
        setNewPasswordUsername(response.username || formData.email);
        setShowNewPasswordModal(true);
        // Don't close login modal yet - will close after password is set
      }
    } catch (err) {
      authLogger.error('Login error:', err);
      
      // Use the utility function to check for UserNotConfirmedException
      if (isUserNotConfirmedException(err)) {
        authLogger.info('User not confirmed, opening email verification modal', { email: formData.email });
        
        // Set registration data for email verification
        setRegistrationData({
          tenantId: '', // Will be set properly if needed
          email: formData.email,
          tenantName: ''
        });
        
        // Set flags to indicate this is from a failed login attempt
        setPendingEmailVerification(true);
        setVerificationFromLogin(true); // New flag specifically for login-to-verification flow
        
        // Close login modal and open email verification modal
        closeLoginModal();
        openEmailVerificationModal();
        
        toast.error('Please verify your email address to complete your account setup.');
        return;
      }
      
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const switchToRegistration = (): void => {
    closeLoginModal();
    openRegistrationModal();
  };

  const switchToForgotPassword = (): void => {
    closeLoginModal();
    openForgotPasswordModal();
  };

  const handleNewPasswordSuccess = async (): Promise<void> => {
    // After successful password change, try to log in the user
    try {
      // The user should now be logged in after completing the challenge
      const response = await authApi.login(newPasswordUsername, formData.password);
      
      if (response.success && response.user) {
        const userData = {
          ...response.user,
          // Ensure we have proper fallbacks
          company: response.user.company || 'N/A',
          role: response.user.role || 'User'
        };

        login(userData);
        
        setShowNewPasswordModal(false);
        closeLoginModal();
        
        // Redirect to app with fresh tokens
        await redirectToApp();
      }
    } catch (err) {
      // If auto-login fails, just close modals and let user login normally
      setShowNewPasswordModal(false);
      closeLoginModal();
    }
  };

  const handleNewPasswordClose = (): void => {
    setShowNewPasswordModal(false);
    setNewPasswordUsername('');
    // Keep login modal open so user can try again
  };

  const handleClose = (): void => {
    closeLoginModal();
    setFormData({ email: '', password: '' });
    setError('');
    setShowPassword(false);
    // Clear the existing user message flag when closing
    setShowExistingUserMessage(false);
    // Clear any prefilled email when closing
    setPrefillEmail('');
  };

  return (
    <>
      <Transition show={isLoginModalOpen}>
      <Dialog onClose={handleClose} className="relative z-50">
        <Transition.Child
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel 
                data-testid="login-modal"
                className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 shadow-xl transition-all"
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    Welcome Back
                  </Dialog.Title>
                  <button
                    data-testid="login-close-button"
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Demo Credentials */}
                {MOCK_MODE && (<div className="mb-6 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
                  <p className="text-sm text-primary-800 dark:text-primary-200 mb-2 font-medium">
                    Demo Credentials:
                  </p>
                  <p className="text-sm text-primary-700 dark:text-primary-300">
                    Email: demo@fabrik28.ai<br />
                    Password: demo123
                  </p>
                </div>)}

                {/* Existing User Info Message */}
                {showExistingUserMessage && (
                  <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      An account with this email already exists. Please log in to continue.
                    </p>
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      data-testid="login-email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter your email"
                    />
                  </div>

                  {/* Password */}
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        name="password"
                        data-testid="login-password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Enter your password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="h-5 w-5" />
                        ) : (
                          <EyeIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div 
                      data-testid="login-error-message"
                      className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
                    >
                      <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    data-testid="login-submit"
                    disabled={isLoading}
                    className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Signing In...' : 'Sign In'}
                  </button>
                </form>

                {/* Forgot Password */}
                <div className="mt-4 text-center">
                  <button 
                    data-testid="forgot-password-link"
                    onClick={switchToForgotPassword}
                    className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors duration-200"
                  >
                    Forgot your password?
                  </button>
                </div>

                {/* Switch to Registration */}
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Don't have an account?{' '}
                    <button
                      onClick={switchToRegistration}
                      className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors duration-200"
                    >
                      Get started for free
                    </button>
                  </p>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>

    {/* New Password Required Modal */}
    <NewPasswordRequiredModal
      isOpen={showNewPasswordModal}
      onClose={handleNewPasswordClose}
      onSuccess={handleNewPasswordSuccess}
      username={newPasswordUsername}
    />
    </>
  );
}
